// URL search-param ↔ filter draft mapping for the property list page.
//
// The property list page is URL-driven: status, ward, listing type,
// bedroom/bathroom counts, area + price ranges, search text, and the
// grid/table view are all carried in {@code window.location.search}.
// Centralizing the parse + serialize logic here keeps the page, filter
// modal, AI extractor, and chip row from drifting.

import type { ListingType, PropertyStatus } from "@/types/property";

export const KNOWN_PROPERTY_STATUSES: readonly PropertyStatus[] = [
  "AVAILABLE",
  "DEPOSIT",
  "SOLD",
  "RENTED",
] as const;

export const KNOWN_LISTING_TYPES: readonly ListingType[] = [
  "SALE",
  "RENT",
] as const;

export type PropertyView = "grid" | "table";

export const PROPERTY_VIEW_OPTIONS: readonly PropertyView[] = ["grid", "table"];

// ---------------------------------------------------------------------------
// Page-size selector
// ---------------------------------------------------------------------------

/**
 * Allowed page sizes. 12 is the default because it fills the responsive
 * grid cleanly at every breakpoint (12 = 1×12 = 2×6 = 3×4). The bigger
 * choices let power users on desktop see more rows per fetch without
 * burning extra round-trips.
 *
 * Stays within the backend's `@Min(1) @Max(100)` validation on
 * {@code PropertyListQuery.size}.
 */
export const PAGE_SIZE_OPTIONS = [12, 24, 48] as const;

export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export const DEFAULT_PAGE_SIZE: PageSize = 12;

/**
 * Snapshot of everything the property list page's URL can carry.
 *
 * Fields are optional because the URL may omit any subset; downstream
 * components decide how to treat absent fields (typically: don't apply
 * the filter).
 */
export interface PropertyFilterDraft {
  status?: PropertyStatus;
  listingType?: ListingType;
  wardCode?: string;
  bedrooms?: number;
  bathrooms?: number;
  areaMin?: number;
  areaMax?: number;
  priceMin?: number;
  priceMax?: number;
  search?: string;
}

// Filter keys whose presence in the URL should be tagged as
// AI-applied. Used by the page-level chip row to highlight which
// filters were inferred. The `search` text param is not in this set —
// it's the regular header search input.
export const AI_FILTER_KEYS = [
  "status",
  "listingType",
  "wardCode",
  "bedrooms",
  "bathrooms",
  "areaMin",
  "areaMax",
  "priceMin",
  "priceMax",
] as const;

// ---------------------------------------------------------------------------
// Parse: URL params → typed draft
// ---------------------------------------------------------------------------

/**
 * Read all known filter fields from a {@link URLSearchParams}. Unknown
 * or invalid values are silently dropped (see {@link parseEnum}).
 */
export function readFilterDraft(params: URLSearchParams): PropertyFilterDraft {
  const draft: PropertyFilterDraft = {};
  const status = parseEnum(
    params.get("status"),
    KNOWN_PROPERTY_STATUSES,
    "status",
  );
  if (status !== undefined) draft.status = status;

  const listingType = parseEnum(
    params.get("listingType"),
    KNOWN_LISTING_TYPES,
    "listingType",
  );
  if (listingType !== undefined) draft.listingType = listingType;

  const ward = params.get("wardCode");
  if (ward !== null && ward !== "") draft.wardCode = ward;

  const search = params.get("search");
  if (search !== null && search !== "") draft.search = search;

  setIfFinite(draft, "bedrooms", params.get("bedrooms"));
  setIfFinite(draft, "bathrooms", params.get("bathrooms"));
  setIfFinite(draft, "areaMin", params.get("areaMin"));
  setIfFinite(draft, "areaMax", params.get("areaMax"));
  setIfFinite(draft, "priceMin", params.get("priceMin"));
  setIfFinite(draft, "priceMax", params.get("priceMax"));

  return draft;
}

/** Read the view selector — defaults to "grid" for unknown / missing values. */
export function readView(params: URLSearchParams): PropertyView {
  const raw = params.get("view");
  return (PROPERTY_VIEW_OPTIONS as readonly string[]).includes(raw ?? "")
    ? (raw as PropertyView)
    : "grid";
}

/**
 * Read the requested page size, falling back to {@link DEFAULT_PAGE_SIZE}
 * for missing, unparseable, or out-of-whitelist values. Always returns
 * one of {@link PAGE_SIZE_OPTIONS}.
 */
export function readPageSize(params: URLSearchParams): PageSize {
  const raw = params.get("size");
  if (raw === null) return DEFAULT_PAGE_SIZE;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_PAGE_SIZE;
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n)
    ? (n as PageSize)
    : DEFAULT_PAGE_SIZE;
}

/**
 * Apply a page-size selection to a {@link URLSearchParams}. Default
 * values are stripped from the URL to keep it clean, and `page` is
 * cleared because the current page may not exist at the new size.
 */
export function writePageSize(params: URLSearchParams, size: PageSize): void {
  if (size === DEFAULT_PAGE_SIZE) {
    params.delete("size");
  } else {
    params.set("size", String(size));
  }
  params.delete("page");
}

// ---------------------------------------------------------------------------
// Serialize: typed draft → URL params
// ---------------------------------------------------------------------------

/**
 * Apply a {@link PropertyFilterDraft} to a {@link URLSearchParams},
 * mutating in place. Absent fields delete their param; present fields
 * stringify their value. Always resets `page` so the user lands on
 * page 0 of the new result set.
 *
 * @param markAi When true, sets `?ai=1` so the page-level chip row
 *               flags these filters as AI-applied.
 */
export function writeFilterDraft(
  params: URLSearchParams,
  draft: PropertyFilterDraft,
  options: { markAi?: boolean } = {},
): void {
  setOrDelete(params, "status", draft.status);
  setOrDelete(params, "listingType", draft.listingType);
  setOrDelete(params, "wardCode", draft.wardCode);
  setOrDelete(
    params,
    "search",
    draft.search?.trim().length === 0 ? undefined : draft.search?.trim(),
  );
  setOrDelete(params, "bedrooms", numberOrUndefined(draft.bedrooms));
  setOrDelete(params, "bathrooms", numberOrUndefined(draft.bathrooms));
  setOrDelete(params, "areaMin", numberOrUndefined(draft.areaMin));
  setOrDelete(params, "areaMax", numberOrUndefined(draft.areaMax));
  setOrDelete(params, "priceMin", numberOrUndefined(draft.priceMin));
  setOrDelete(params, "priceMax", numberOrUndefined(draft.priceMax));

  if (options.markAi === true) {
    params.set("ai", "1");
  } else {
    params.delete("ai");
  }
  params.delete("page");
}

/** Returns true when {@code draft} has at least one filter set. */
export function isFilterDraftEmpty(draft: PropertyFilterDraft): boolean {
  return (
    draft.status === undefined &&
    draft.listingType === undefined &&
    (draft.wardCode === undefined || draft.wardCode === "") &&
    draft.bedrooms === undefined &&
    draft.bathrooms === undefined &&
    draft.areaMin === undefined &&
    draft.areaMax === undefined &&
    draft.priceMin === undefined &&
    draft.priceMax === undefined &&
    (draft.search === undefined || draft.search === "")
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseEnum<T extends string>(
  raw: string | null,
  allowed: readonly T[],
  paramName: string,
): T | undefined {
  if (raw === null || raw === "") return undefined;
  if ((allowed as readonly string[]).includes(raw)) {
    return raw as T;
  }
  if (import.meta.env.DEV) {
    console.warn(
      `[property-filter-params] ignoring unknown ${paramName}=${raw}; expected one of ${allowed.join("|")}`,
    );
  }
  return undefined;
}

function setIfFinite(
  draft: PropertyFilterDraft,
  key: keyof PropertyFilterDraft,
  raw: string | null,
): void {
  if (raw === null || raw === "") return;
  const n = Number(raw);
  if (!Number.isFinite(n)) return;
  (draft as Record<string, number>)[key] = n;
}

function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | number | undefined,
): void {
  if (value === undefined) {
    params.delete(key);
  } else {
    params.set(key, String(value));
  }
}

function numberOrUndefined(value: number | undefined): number | undefined {
  return value !== undefined && Number.isFinite(value) ? value : undefined;
}
